import React, { useState } from 'react'
import { formatPrice } from 'formatters'
import { getValue } from 'remote-config-value'
import { makeStyles } from '@material-ui/core/styles'
import { Alert, Checkbox, Skeleton, Typography } from 'foundations-library/components'
import StepTitle from './StepTitle'
import PaymentErrorDialog from './PaymentErrorDialog'

const useStyles = makeStyles((theme) => {
    const { colors, typography } = theme['foundations-library']

    return {
        invoice: {
            '& .skeleton': {
                height: 24,
                borderRadius: 8
            }
        },
        mb40: {
            marginBottom: 40
        },
        alert: {
            textAlign: 'left',
            marginBottom: 40
        },
        grayishBackground: {
            backgroundColor: colors.neutral[50]
        },
        lightBorderBottom: {
            boxShadow: '0 -1px 0 0 rgba(133, 146, 154, 0.20) inset'
        },
        darkerBorderBottom: {
            boxShadow: '0 -1px 0 0 #BFBEBE inset'
        },
        responsiveTableWrapper: {
            overflowX: 'auto',
            borderRadius: '5px 0 0 5px',
            border: 'none',
            width: '100%',
            '& table': {
                width: '100%'
            },
            '& td': {
                height: 70,
                paddingLeft: 10,
                ...typography.subtitle2
            },
            '& th': {
                padding: '0 10px',
                height: 43,
                ...typography.body3
            }
        },
        charges: {
            marginBottom: 10,
            marginTop: 10
        }
    }
})

type PropsType = {
    showAlert?: boolean
    alertID?: string
    payment: Record<string, unknown>
    paymentInfoFetched: boolean
    fetchEnrollmentPaymentError: boolean
    onClickRetry(): unknown
    onCancel(): unknown
} | null

const TextSkeleton = () => <Skeleton variant='text' animation='wave'/>

const LoadingBody = (classes) => {
    return <tbody>
        <tr className={classes.lightBorderBottom}>
            <td>
                <TextSkeleton />
            </td>
            <td>
                <TextSkeleton />
            </td>
            <td><TextSkeleton /></td>
            <td><TextSkeleton /></td>
        </tr>
        <tr>
            <td colSpan='3'><TextSkeleton /></td>
            <td><TextSkeleton /></td>
        </tr>
        <tr className={classes.darkerBorderBottom}>
            <td colSpan='3'><TextSkeleton /></td>
            <td><TextSkeleton /></td>
        </tr>
        <tr className={classes.lightBorderBottom}>
            <td colSpan='3'>
                <TextSkeleton />
            </td>
            <td>
                <TextSkeleton />
            </td>
        </tr>
    </tbody>
}

const TableBody = ({
    classes,
    payment
}: {
    classes: Record<string, unknown>,
    payment: Pick<PropsType, 'payment'>
}) => {
    const {
        transactions = [],
        tax_transactions = [],
        total_fees_as_string = ''
    } = payment

    const transactionsGroupedByCode = transactions.reduce((a, c) => {
        if (!a[c.code]) a[c.code] = []
        a[c.code].push(c)
        return a
    }, {})

    const getTotal = (arr) => {
        return arr.reduce((a, b) => {
            a = a + b.amount
            return a
        }, 0)
    }
    return <tbody>
        {Object.entries(transactionsGroupedByCode).map(([code, codeGroup], index) => {
            return <tr className={index === transactions.length - 1 && classes.lightBorderBottom} key={index}>
                <td>
                    {payment.labels[`${code}`]}
                </td>
                <td>
                    {formatPrice(codeGroup[0].amount)}
                </td>
                <td>{codeGroup.length}</td>
                <td>{formatPrice(codeGroup.reduce((a, c) => {
                    a = a + c.amount
                    return a
                }, 0))}</td>
            </tr>
        })}
        <tr>
            <td colSpan='3'>{getValue('sms_enrollment_subtotal')}</td>
            <td>{formatPrice(getTotal(transactions))}</td>
        </tr>
        <tr className={classes.darkerBorderBottom}>
            <td colSpan='3'>Taxes</td>
            <td>{formatPrice(getTotal(tax_transactions))}</td>
        </tr>
        <tr className={classes.lightBorderBottom}>
            <td colSpan='3'>
                {getValue('sms_enrollment_total_charges_credit_card')}
            </td>
            <td>
                {total_fees_as_string}
            </td>
        </tr>
    </tbody>
}

/**
 * @param {object} props
 */
const InvoiceStep = ({
    payment = {},
    showAlert,
    alertID,
    confirm,
    showConfirmation,
    onChangeHandler,
    paymentInfoFetched,
    fetchEnrollmentPaymentError,
    onClickRetry,
    onCancel
}: PropsType) => {
    const classes = useStyles()

    const isLoading = !paymentInfoFetched || fetchEnrollmentPaymentError
    const [retriedTimes, setRetriedTimes] = useState(0)
    const handleClickRetry = () => {
        setRetriedTimes(prev => prev + 1)
        onClickRetry?.()
    }

    return (
        <div className={classes.invoice} data-testid='invoice-step'>
            <StepTitle remoteConfigID='sms_enrollment_details' />
            {
                showAlert &&
                <Alert level='warning' textAlign='left' className={classes.alert}>{getValue(alertID)}</Alert>
            }
            <div>
                <div className={classes.responsiveTableWrapper}>
                    <table data-testid='invoice-step-table'>
                        <thead>
                            <tr className={`${classes.grayishBackground}`}>
                                <th>{getValue('sms_enrollment_charges_and_credits')}</th>
                                <th>{getValue('sms_enrollment_unit_price')}</th>
                                <th>{getValue('sms_enrollment_quantity')}</th>
                                <th>{getValue('sms_enrollment_amount')}</th>
                            </tr>
                        </thead>
                        {
                            isLoading
                                ? <LoadingBody classes={classes}/>
                                : <TableBody payment={payment} classes={classes} data-testid='invoice-step-table-body'/>
                        }
                    </table>
                    <Typography variant='body2' remoteConfigID='sms_enrollment_all_charges_appear_on_statement' className={classes.charges} data-testid='invoice-step-charges-appear'/>
                    <Typography variant='subtitle2' remoteConfigID='sms_enrollment_excludes_account_credit_adjustments' className={classes.mb40} data-testid='invoice-step-credit-adjustment'/>
                </div>
                {
                    showConfirmation && !isLoading &&
                    <Checkbox
                        id='confirmation_checkbox'
                        checked={confirm}
                        onChange={onChangeHandler}
                        label={getValue('sms_enrollment_business_acknowledgement')}
                        data-testid='invoice-step-checkbox'
                    />
                }
                <PaymentErrorDialog
                    isOpen={fetchEnrollmentPaymentError}
                    onRetry={handleClickRetry}
                    onCancel={retriedTimes > 0 ? onCancel : undefined}
                    data-testid='invoice-step-payment-dialog'
                />
            </div>
        </div>
    )
}

export default InvoiceStep
